import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { ThemedButton } from "@/components/themed";
import {
  checkKujiCardCheckoutStatus,
  type KujiCardCheckoutStatus,
} from "@/services/customerService";
import LotteryAnimation from "@/components/LotteryAnimation";
import { trackPurchase } from "@/utils/customerAnalytics";
import { Loader2, CheckCircle2, XCircle, Crown, Sparkles, Package } from "lucide-react";

type KujiCardResultPageProps = {
  siteId: number;
};

const KujiCardResultPage: React.FC<KujiCardResultPageProps> = ({ siteId }) => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const paymentUid = searchParams.get("payment_uid");

  const [loading, setLoading] = useState(true);
  const [checkoutStatus, setCheckoutStatus] = useState<KujiCardCheckoutStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [eventUid, setEventUid] = useState<string | null>(null); // リトライ用
  const [pollCount, setPollCount] = useState(0);
  const [animationPhase, setAnimationPhase] = useState(false);
  const [animationIndex, setAnimationIndex] = useState(0);
  const [pendingStatus, setPendingStatus] = useState<KujiCardCheckoutStatus | null>(null);
  const [resolved, setResolved] = useState(false);

  const animationKey = paymentUid ? `animation_played_${paymentUid}` : null;
  const alreadyAnimated = animationKey ? sessionStorage.getItem(animationKey) === "1" : false;

  useEffect(() => {
    if (resolved) return;

    if (status === "failure") {
      setLoading(false);
      setError("決済に失敗しました。もう一度お試しください。");
      setResolved(true);
      return;
    }

    if (!paymentUid) {
      setLoading(false);
      setError("決済情報が見つかりません。");
      setResolved(true);
      return;
    }

    const pollStatus = async () => {
      try {
        const result = await checkKujiCardCheckoutStatus(siteId, paymentUid);

        if (result.status === "completed") {
          setLoading(false);
          setResolved(true);
          // 広告コンバージョン: くじ購入完了（決済確定 = ポーリング completed 地点で発火）。
          // value は card_amount（その取引の現金分）。total_paid だとポイント利用分が
          // ポイント購入時の計測と二重計上になるため card_amount を使う。
          trackPurchase({ value: result.summary?.card_amount ?? 0, transactionId: paymentUid ?? undefined });
          // 等級ごとのアニメーションまたはイベント全体のアニメーションがあれば再生
          const hasAnyAnimation = result.lottery_animation_url || result.results?.some((r: any) => r.grade_animation_url);
          if (!alreadyAnimated && result.results && result.results.length > 0 && hasAnyAnimation) {
            setPendingStatus(result);
            setAnimationIndex(0);
            setAnimationPhase(true);
          } else {
            setCheckoutStatus(result);
          }
        } else if (result.status === "failed" || result.status === "cancelled") {
          setLoading(false);
          setResolved(true);
          setEventUid(result.event_uid || null);
          setError(
            result.status === "cancelled"
              ? "在庫が不足しているため、決済がキャンセルされました。カードへの請求は発生しません。"
              : result.error_message || "決済処理に失敗しました。"
          );
        } else if (pollCount >= 30) {
          setLoading(false);
          setResolved(true);
          setError("処理がタイムアウトしました。しばらくしてからマイコレクションをご確認ください。");
        } else {
          setPollCount((c) => c + 1);
        }
      } catch (err: any) {
        if (pollCount >= 5) {
          setLoading(false);
          setResolved(true);
          setError("ステータスの取得に失敗しました。");
        } else {
          setPollCount((c) => c + 1);
        }
      }
    };

    const interval = setInterval(pollStatus, 1000);
    pollStatus();

    return () => clearInterval(interval);
  }, [paymentUid, status, pollCount, siteId, resolved, alreadyAnimated]);

  // アニメーション再生フェーズ
  if (animationPhase && pendingStatus) {
    const totalAnimations = pendingStatus.results!.length;

    const handleAnimationEnd = () => {
      const nextIndex = animationIndex + 1;
      if (nextIndex >= totalAnimations) {
        if (animationKey) sessionStorage.setItem(animationKey, "1");
        setAnimationPhase(false);
        setCheckoutStatus(pendingStatus);
        setPendingStatus(null);
      } else {
        setAnimationIndex(nextIndex);
      }
    };

    const handleSkipAll = () => {
      if (animationKey) sessionStorage.setItem(animationKey, "1");
      setAnimationPhase(false);
      setCheckoutStatus(pendingStatus);
      setPendingStatus(null);
    };

    // 等級ごとのアニメーションURLを優先、なければイベント全体にフォールバック
    const currentResult = pendingStatus.results![animationIndex];
    const currentAnimationUrl = (currentResult as any)?.grade_animation_url || pendingStatus.lottery_animation_url;

    if (!currentAnimationUrl) {
      // アニメーションがない場合はスキップして次へ（useEffectで非同期に実行）
      setTimeout(handleAnimationEnd, 0);
      return null;
    }

    return (
      <LotteryAnimation
        animationUrl={currentAnimationUrl}
        isPlaying={true}
        onAnimationEnd={handleAnimationEnd}
        onSkipAll={handleSkipAll}
        gachaCount={totalAnimations}
        currentIndex={animationIndex}
        totalCount={totalAnimations}
      />
    );
  }

  // ローディング中
  if (loading) {
    return (
      <>
        <Helmet>
          <title>処理中...</title>
        </Helmet>
        <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mb-4" />
          <p className="text-lg font-bold">スクラッチ結果を処理中...</p>
          <p className="text-sm text-gray-500 mt-2">しばらくお待ちください</p>
        </div>
      </>
    );
  }

  // エラー
  if (error) {
    return (
      <>
        <Helmet>
          <title>エラー</title>
        </Helmet>
        <div className="max-w-screen-md mx-auto px-4 py-8 pt-24 bg-theme-bg min-h-screen">
          <div className="bg-white rounded-lg border-2 border-black p-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">購入できませんでした</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex flex-col gap-3">
              {eventUid && (
                <Link to={`/kuji/${eventUid}`}>
                  <ThemedButton className="w-full">商品ページに戻る</ThemedButton>
                </Link>
              )}
              <Link to="/">
                <Button variant="outline" className="w-full">トップに戻る</Button>
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // 成功
  if (checkoutStatus?.status === "completed" && checkoutStatus.results) {
    return (
      <>
        <Helmet>
          <title>購入完了</title>
        </Helmet>
        <div className="max-w-screen-md mx-auto px-4 py-8 pt-24 bg-theme-bg min-h-screen">
          <div className="bg-white rounded-lg border-2 border-black p-6 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">購入が完了しました！</h1>
            <p className="text-gray-600 mb-6">
              {checkoutStatus.summary?.total_quantity}枚のスクラッチを引きました。マイコレクションから確認が可能です。
            </p>

            {/* 結果サマリー */}
            <div className="mb-6 text-left">
              <h2 className="font-bold mb-3">当選結果</h2>
              {(() => {
                const specialItems = checkoutStatus.results.filter(
                  (item) => item.is_last_one_prize || item.grade_name === 'ラストワン賞'
                );
                const normalItems = checkoutStatus.results.filter(
                  (item) => !item.is_last_one_prize && item.grade_name !== 'ラストワン賞'
                );
                const grouped = normalItems.reduce<Record<string, { item: typeof normalItems[0]; count: number }>>((acc, item) => {
                  const key = `${item.product_name}__${item.grade_name}`;
                  if (acc[key]) {
                    acc[key].count += 1;
                  } else {
                    acc[key] = { item, count: 1 };
                  }
                  return acc;
                }, {});

                const sortedGrouped = Object.values(grouped).sort(
                  (a, b) => (a.item.grade_position ?? 999) - (b.item.grade_position ?? 999)
                );

                return (
                  <>
                    {/* カードグリッド（ラストワン賞 + 通常の賞） */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {/* ラストワン賞 */}
                      {specialItems.map((item, index) => (
                        <div
                          key={`special-${index}`}
                          className="relative flex flex-col p-2 rounded-lg border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 shadow-[0_0_10px_rgba(251,191,36,0.2)]"
                        >
                          <Sparkles className="absolute top-1 right-1 w-3 h-3 text-yellow-400 animate-pulse" />
                          <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                            {item.image_url ? (
                              <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-gray-300" />
                              </div>
                            )}
                          </div>
                          <div className="mt-2">
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">
                              <Crown className="w-3 h-3 inline mr-0.5" />ラストワン賞
                            </span>
                            <p className="text-sm font-bold text-gray-900 mt-1 line-clamp-2">{item.product_name || "景品"}</p>
                          </div>
                        </div>
                      ))}
                      {sortedGrouped.map(({ item, count }, index) => {
                        const isBulkBonus = item.grade_name === 'セット購入特典';
                        return (
                          <div key={`grouped-${index}`} className="flex flex-col p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                            {/* 商品画像 */}
                            <div className="aspect-square bg-gray-100 rounded overflow-hidden">
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-8 h-8 text-gray-300" />
                                </div>
                              )}
                            </div>
                            {/* 等級バッジ + 商品名 */}
                            <div className="mt-2">
                              <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isBulkBonus ? 'bg-blue-50 text-blue-600' : 'bg-orange-50 text-orange-600'}`}>
                                {item.grade_name}
                              </span>
                              <p className="text-sm font-bold text-gray-900 mt-1 line-clamp-2">{item.product_name || "景品"}</p>
                            </div>
                            {/* 当選枚数 */}
                            <div className="mt-auto pt-2">
                              <div className="text-center py-1 bg-gray-50 rounded text-sm font-bold text-gray-700">
                                ×{count}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* 支払い情報 */}
            {checkoutStatus.summary && (
              <div className="bg-orange-50 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between text-sm mb-1">
                  <span>スクラッチ代（¥{Math.round(checkoutStatus.summary.kuji_cost / checkoutStatus.summary.total_quantity).toLocaleString()} × {checkoutStatus.summary.total_quantity}枚）</span>
                  <span>¥{checkoutStatus.summary.kuji_cost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>{checkoutStatus.shipping_fee_label === 'system_fee' ? 'システム手数料' : '配送料'}</span>
                  <span>¥{checkoutStatus.summary.shipping_fee.toLocaleString()}</span>
                </div>
                {checkoutStatus.summary.points_used > 0 && (
                  <>
                    <div className="flex justify-between text-sm mb-1 border-t pt-2">
                      <span>小計</span>
                      <span>¥{checkoutStatus.summary.total_paid.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1 text-blue-600">
                      <span>ポイント利用</span>
                      <span>-{checkoutStatus.summary.points_used.toLocaleString()}pt</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>カード決済</span>
                      <span>¥{checkoutStatus.summary.card_amount.toLocaleString()}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>合計</span>
                  <span className="text-orange-600">¥{checkoutStatus.summary.total_paid.toLocaleString()}</span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {checkoutStatus.event_uid && (
                <Link to={`/kuji/${checkoutStatus.event_uid}`}>
                  <ThemedButton className="w-full">もう一度購入する</ThemedButton>
                </Link>
              )}
              <Link to="/products">
                <Button variant="outline" className="w-full">
                  マイコレクションを確認する
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default KujiCardResultPage;
